import { clearProfile } from "actions/profile";
import { clearSites } from "actions/site";
import { clearRegistration } from "actions/registration";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn,
  signOut,
  updatePassword,
  updateUserAttributes,
} from "aws-amplify/auth";
import { setLastRefreshTime } from "utils/indexDbStorage";
import {
  removeSession,
  getClearStorageTime,
  removeClearStorage,
} from "utils/localStorage";
import { mockCognitoUser } from "mock/session";

export const LOGIN_REQUEST = "@@session/LOGIN_REQUEST";
export const LOGIN_SUCCESS = "@@session/LOGIN_SUCCESS";
export const LOGIN_FAILURE = "@@session/LOGIN_FAILURE";

export function login(formData) {
  return async (dispatch) => {
    dispatch(clearProfile());
    dispatch(clearSites());
    dispatch(clearRegistration());
    dispatch({
      type: LOGIN_REQUEST,
      error: {},
    });

    const useMock = process.env.REACT_APP_USE_MOCK === "true";
    if (useMock) {
      dispatch({
        type: LOGIN_SUCCESS,
        payload: mockCognitoUser(),
      });
      setLastRefreshTime(new Date().getTime());
      return;
    }

    try {
      try {
        await signOut();
      } catch (e) {
        // Ignore errors if no user was signed in
      }

      const signInResponse = await signIn({
        username: formData.email,
        password: formData.password?.trim(),
      });

      const signInStep = signInResponse.nextStep.signInStep;

      if (signInStep !== "DONE") {
        dispatch(setChallenge(signInStep));
      }

      //RESET_PASSWORD
      if (signInStep === "RESET_PASSWORD") {
        // User must have already got the code
      } else if (signInStep === "DONE") {
        dispatch(setChallenge(""));

        const response = await getUserSession();
        dispatch({
          type: LOGIN_SUCCESS,
          payload: response,
        });
        setLastRefreshTime(new Date().getTime());
      }
    } catch (error) {
      dispatch({
        type: LOGIN_FAILURE,
        payload: error,
      });
    }
  };
}

export const CHANGE_PASSWORD_REQUEST = "@@session/CHANGE_PASSWORD_REQUEST";
export const CHANGE_PASSWORD_SUCCESS = "@@session/CHANGE_PASSWORD_SUCCESS";
export const CHANGE_PASSWORD_FAILURE = "@@session/CHANGE_PASSWORD_FAILURE";

export function changePassword(formData) {
  return async (dispatch) => {
    dispatch({
      type: CHANGE_PASSWORD_REQUEST,
    });

    try {
      await updatePassword({
        oldPassword: formData?.oldPassword,
        newPassword: formData?.newPassword,
      });

      const response = await getUserSession();

      dispatch({
        type: CHANGE_PASSWORD_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: CHANGE_PASSWORD_FAILURE,
        payload: error,
      });
    }
  };
}

export const UPDATE_DETAILS_REQUEST = "@@session/UPDATE_DETAILS_REQUEST";
export const UPDATE_DETAILS_SUCCESS = "@@session/UPDATE_DETAILS_SUCCESS";
export const UPDATE_DETAILS_FAILURE = "@@session/UPDATE_DETAILS_FAILURE";

export function updateDetails(formData) {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_DETAILS_REQUEST,
    });
    try {
      await confirmSignIn({
        challengeResponse: formData.password.trim(),
      });

      await updateUserAttributes({
        userAttributes: {
          given_name: formData.firstName,
          family_name: formData.lastName,
        },
      });

      dispatch(setChallenge(""));

      const response = await getUserSession();

      dispatch({
        type: UPDATE_DETAILS_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: UPDATE_DETAILS_FAILURE,
        payload: error,
      });
    }
  };
}

export const LOGOUT_REQUEST = "@@session/LOGOUT_REQUEST";
export const LOGOUT_SUCCESS = "@@session/LOGOUT_SUCCESS";
export const LOGOUT_FAILURE = "@@session/LOGOUT_FAILURE";

export function logout() {
  return async (dispatch) => {
    dispatch({
      type: LOGOUT_REQUEST,
    });
    removeSession();
    dispatch(clearProfile());
    dispatch(clearSites());
    dispatch(clearRegistration());
    try {
      await signOut();
      dispatch({
        type: LOGOUT_SUCCESS,
      });
    } catch (error) {
      dispatch({
        type: LOGOUT_FAILURE,
        payload: error,
      });
    }
  };
}

export const FORGOT_PASSWORD_REQUEST = "@@session/FORGOT_PASSWORD_REQUEST";
export const FORGOT_PASSWORD_SUCCESS = "@@session/FORGOT_PASSWORD_SUCCESS";
export const FORGOT_PASSWORD_FAILURE = "@@session/FORGOT_PASSWORD_FAILURE";

export function forgotPassword(username) {
  return async (dispatch) => {
    dispatch({
      type: FORGOT_PASSWORD_REQUEST,
    });
    try {
      const response = await resetPassword({
        username,
      });
      dispatch({
        type: FORGOT_PASSWORD_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: FORGOT_PASSWORD_FAILURE,
        payload: error,
      });
    }
  };
}

export const FORGOT_PASSWORD_SUBMIT_REQUEST =
  "@@session/FORGOT_PASSWORD_SUBMIT_REQUEST";
export const FORGOT_PASSWORD_SUBMIT_SUCCESS =
  "@@session/FORGOT_PASSWORD_SUBMIT_SUCCESS";
export const FORGOT_PASSWORD_SUBMIT_FAILURE =
  "@@session/FORGOT_PASSWORD_SUBMIT_FAILURE";

export function forgotPasswordSubmit(formData) {
  return async (dispatch) => {
    dispatch({
      type: FORGOT_PASSWORD_SUBMIT_REQUEST,
    });
    try {
      const response = await confirmResetPassword({
        username: formData.email,
        confirmationCode: formData.code,
        newPassword: formData.password,
      });

      dispatch({
        type: FORGOT_PASSWORD_SUBMIT_SUCCESS,
        payload: response,
      });
      dispatch(
        login({
          email: formData.email,
          password: formData.password,
        })
      );
    } catch (error) {
      dispatch({
        type: FORGOT_PASSWORD_SUBMIT_FAILURE,
        payload: error,
      });
    }
  };
}

export const RESET_FORGOT_PASSWORD_FLAG =
  "@@session/RESET_FORGOT_PASSWORD_FLAG";

export function resetForgotPasswordFlag() {
  return async (dispatch) => {
    dispatch({
      type: RESET_FORGOT_PASSWORD_FLAG,
    });
  };
}

export const CLEAR_SESSION_ERROR = "@@session/CLEAR_SESSION_ERROR";
export function clearSessionError() {
  return async (dispatch) => {
    dispatch({
      type: CLEAR_SESSION_ERROR,
    });
  };
}

export function clearAllSessionData() {
  return async (dispatch) => {
    removeSession();
    dispatch(clearProfile());
    dispatch(clearSites());
    dispatch(clearRegistration());
    dispatch({
      type: LOGOUT_SUCCESS,
    });
  };
}

export function checkStorage() {
  return async (dispatch) => {
    const time = getClearStorageTime();
    if (!time) {
      removeClearStorage();
      dispatch(clearAllSessionData());
    }
  };
}

export const SET_CHALLENGE = "@@session/SET_CHALLENGE";
export function setChallenge(value) {
  return async (dispatch) => {
    dispatch({
      type: SET_CHALLENGE,
      payload: value,
    });
  };
}

export const getUserSession = async () => {
  const session = await fetchAuthSession({ forceRefresh: true });

  const response = {
    ...session,
    signInUserSession: session.tokens,
  };

  return response;
};

export function retryFetchSession(maxAttempts = 1, delayMs = 1000) {
  return async (dispatch) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        const session = await fetchAuthSession({ forceRefresh: true });

        if (session?.tokens) {
          return session;
        }
        console.log("Logging out because no tokens after retryFetchSession")
        dispatch(logout());
        return null;
      } catch (error) {
        if (attempt === maxAttempts - 1) {
          return null;
        }
      }
    }
  };
}
